import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp, Pause, Play, X, Volume2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { usePlayer } from "@/context/PlayerContext";
import { useLang } from "@/context/LangContext";
import { STATION_LOGO } from "@/lib/useRadioStatus";

// Current song's cover; fades when the song changes and falls back to the station logo.
// `large` shows it as a full album-cover square above the controls.
const LiveArt = ({ src, large }) => (
  <div
    data-testid={large ? "mini-player-artwork-large" : "mini-player-artwork-small"}
    style={large ? { width: "min(100%, 380px, 46vh)" } : undefined}
    className={`relative shrink-0 overflow-hidden bg-[#0B111E] ${large ? "mx-auto mb-3 aspect-square rounded-2xl shadow-[0_24px_60px_-20px_rgba(0,0,0,0.75)]" : "h-14 w-14 rounded-xl shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)] sm:h-16 sm:w-16"}`}
  >
    <AnimatePresence initial={false}>
      <motion.img
        key={src || STATION_LOGO}
        data-testid="mini-player-artwork"
        src={src || STATION_LOGO}
        onError={(e) => { if (!e.currentTarget.src.endsWith(STATION_LOGO)) e.currentTarget.src = STATION_LOGO; }}
        alt="Now playing artwork"
        initial={{ opacity: 0, scale: 1.08 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
        className="absolute inset-0 h-full w-full object-cover"
      />
    </AnimatePresence>
  </div>
);

const Bars = ({ active }) => (
  <span className="flex h-5 items-end gap-[3px]" aria-hidden>
    {[0, 1, 2, 3, 4].map((i) => (
      <span key={i} className={`eq-bar w-[3px] rounded-full bg-[#D4AF37] ${active ? "is-playing" : ""}`} style={{ animationDelay: `${i * 0.13}s` }} />
    ))}
  </span>
);

export const MiniPlayer = () => {
  const { track, playing, toggle, close, volume, setVolume, radio } = usePlayer();
  const { t, pick } = useLang();
  const liveSong = track?.live && radio?.online ? radio : null;
  const [big, setBig] = useState(true);
  return (
    <AnimatePresence>
      {track && (
        <motion.div
          data-testid="mini-player-bar"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 26 }}
          className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-3xl rounded-2xl border border-[#D4AF37]/25 bg-[#131C2E]/85 p-3 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl md:bottom-5"
        >
          {track.live && big && <LiveArt src={liveSong?.artwork} large />}
          <div className="flex items-center gap-3">
            {track.live && !big && <LiveArt src={liveSong?.artwork} />}
            <button
              data-testid={playing ? "mini-player-pause-btn" : "mini-player-play-btn"}
              onClick={toggle}
              className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#D4AF37] text-[#0B111E] transition-transform duration-300 hover:scale-105"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 translate-x-[1px]" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {track.live && (
                  <span data-testid="mini-player-live-badge" className="flex items-center gap-1.5 rounded-full bg-red-500/15 px-2 py-0.5 font-mono text-[10px] font-semibold tracking-widest text-red-300">
                    <span className="live-dot !h-1.5 !w-1.5" /> {t("player.live")}
                  </span>
                )}
                <Bars active={playing} />
              </div>
              <p data-testid="mini-player-title" className="mt-1 truncate font-ui text-sm font-semibold text-[#F4EEE2]">
                {track.live ? liveSong?.title || track.title : pick(track.title, track.title_mh)}
              </p>
              {track.speaker && <p className="truncate text-xs text-[#B7BFCC]">{track.speaker}</p>}
              {liveSong?.title && (
                <p data-testid="mini-player-artist" className="truncate text-xs text-[#B7BFCC]">
                  {liveSong.artist || track.title}
                </p>
              )}
            </div>
            <div className="hidden w-32 items-center gap-2 sm:flex">
              <Volume2 className="h-4 w-4 text-[#B7BFCC]" />
              <Slider data-testid="mini-player-volume-slider" value={[volume * 100]} max={100} step={1} onValueChange={([v]) => setVolume(v / 100)} />
            </div>
            {track.live && (
              <button
                data-testid="mini-player-art-toggle"
                onClick={() => setBig((b) => !b)}
                className="rounded-full p-2 text-[#B7BFCC] transition-colors hover:text-[#F4EEE2]"
                aria-label={big ? "Make artwork smaller" : "Show big artwork"}
              >
                {big ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </button>
            )}
            <button data-testid="mini-player-close-btn" onClick={close} className="rounded-full p-2 text-[#B7BFCC] transition-colors hover:text-[#F4EEE2]" aria-label="Close player">
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
